import React, { useCallback, useEffect, useState } from 'react';
import { FormField, Input, Select, Textarea } from '../common/Form';
import Button from '../common/Button';
import {
  createSessionSeries,
  getSessionSeries,
  listSessionSeries,
} from '../../services/sessionSeriesService';
import { CREATE_SERIES_VALUE, seriesIdOrNull } from '../../utils/sessionSeries';

const SessionSeriesField = ({
  clubId,
  value,
  onChange,
  error,
}) => {
  const [series, setSeries] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [createError, setCreateError] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadSeries = useCallback(async () => {
    if (!clubId) {
      setSeries([]);
      return;
    }
    try {
      const rows = await listSessionSeries(clubId);
      setSeries(rows);
    } catch (err) {
      console.error('Error loading session series:', err);
    }
  }, [clubId]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  useEffect(() => {
    if (!value || !clubId) return;
    if (series.some((row) => row.id === value)) return;
    let cancelled = false;
    getSessionSeries(value)
      .then((row) => {
        if (!cancelled && row) {
          setSeries((prev) => (prev.some((item) => item.id === row.id) ? prev : [row, ...prev]));
        }
      })
      .catch((err) => {
        console.error('Error loading assigned session series:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [value, clubId, series]);

  const options = [
    ...series.map((row) => ({
      value: row.id,
      label: row.is_active === false ? `${row.name} (archived)` : row.name,
    })),
    { value: CREATE_SERIES_VALUE, label: 'Create new…' },
  ];

  const handleSelect = (event) => {
    const next = event.target.value;
    if (next === CREATE_SERIES_VALUE) {
      setCreating(true);
      setCreateError(null);
      return;
    }
    setCreating(false);
    onChange(seriesIdOrNull(next));
  };

  const handleCancelCreate = () => {
    setCreating(false);
    setNewName('');
    setNewDescription('');
    setCreateError(null);
  };

  const handleCreate = async () => {
    setSaving(true);
    setCreateError(null);
    try {
      const created = await createSessionSeries({
        clubId,
        name: newName,
        description: newDescription,
      });
      setSeries((prev) =>
        [...prev.filter((row) => row.id !== created.id), created].sort((a, b) =>
          a.name.localeCompare(b.name)
        )
      );
      onChange(created.id);
      handleCancelCreate();
    } catch (err) {
      setCreateError(err.message || 'Failed to create series');
    } finally {
      setSaving(false);
    }
  };

  if (!clubId) return null;

  return (
    <div className="space-y-3">
      <FormField
        label="Series"
        helperText="Standing night this date belongs to, e.g. Classic Tuesday."
        error={error}
      >
        <Select
          name="session_series_id"
          value={creating ? CREATE_SERIES_VALUE : value || ''}
          onChange={handleSelect}
          placeholder="None"
          options={options}
        />
      </FormField>
      {creating ? (
        <div className="space-y-3 rounded-lg border border-border-medium bg-surface-subtle p-3">
          <FormField label="Series name" required error={createError}>
            <Input
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="e.g. Classic Tuesday"
              autoFocus
            />
          </FormField>
          <FormField label="Description">
            <Textarea
              value={newDescription}
              onChange={(event) => setNewDescription(event.target.value)}
              placeholder="Optional"
              rows={2}
            />
          </FormField>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" size="small" onClick={handleCancelCreate} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" variant="primary" size="small" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating…' : 'Create series'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SessionSeriesField;
